import { ComponentType } from 'react';
import { AnalyticsActionType } from '@/shared/analytics/export';
import { SearchResultItem } from '@/core/search/model/search';
import { SearchFilterType } from '@/core/search/contexts/search-context';

// Who is acting, for providers that record their own analytics when they open an item.
export interface SearchOpenContext {
  subject: object | null;
  sessionId: string | null;
}

// What a feature module adds to global search for one result type. Search itself stays
// type-agnostic; providers are registered from the composition root (src/app).
export interface SearchResultTypeProvider {
  /** Builds the analytics `object` recorded for a search action on an item of this type. */
  analyticsObject?(item: SearchResultItem, actionType: AnalyticsActionType): Promise<object>;
  /** Extra inline actions shown next to the item, e.g. the article bookmark button. */
  ItemActions?: ComponentType<{
    item: SearchResultItem;
    recordAction(actionType: AnalyticsActionType): void;
  }>;
  /** Resolves the id that `Preview` shows for an item (e.g. article slug → article uri). */
  preview?(item: SearchResultItem): Promise<string>;
  /** Opens an item that has no preview modal (e.g. a PDF in the in-app viewer). */
  open?(item: SearchResultItem, context: SearchOpenContext): void;
  /** Whether `open` can act on this item; when it returns false no open control is offered. */
  canOpen?(item: SearchResultItem): boolean;
  /** Shows a previewed item (e.g. the article modal). Always mounted; `id` is set while open. */
  Preview?: ComponentType<{ id?: string; onClose(): void }>;
}

const providers = new Map<SearchFilterType, SearchResultTypeProvider>();

export function registerSearchResultType(
  type: SearchFilterType,
  provider: SearchResultTypeProvider,
) {
  providers.set(type, provider);
}

export function unregisterSearchResultType(type: SearchFilterType) {
  providers.delete(type);
}

export function getSearchResultType(
  type: SearchFilterType | null,
): SearchResultTypeProvider | undefined {
  return type ? providers.get(type) : undefined;
}

export function getSearchResultTypes(): [SearchFilterType, SearchResultTypeProvider][] {
  return [...providers];
}
